import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Page lines → markup → discounts → quote total ex/incl GST, same maths as the Quote PDF.
export function QuoteTotalStrip({ api, appraisalId, linesTotal, label = 'Lines on this page', testid = 'quote-total-strip' }) {
  const [s, setS] = useState(null);
  const load = useCallback(() => {
    if (!api || !appraisalId) return;
    api.get(`/quote-summary/${appraisalId}`).then(r => setS(r.data)).catch(() => {});
  }, [api, appraisalId]);
  useEffect(() => { load(); }, [load, linesTotal]);
  useEffect(() => {
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load]);
  if (!s) return null;
  const lines = Number(linesTotal || 0);
  const markup = Number(s.markup || 1) || 1;
  const marked = lines * markup;
  const discounts = s.discounts || [];
  const extra = Number(s.extra_discount_amount || 0);
  const exGst = Math.max(0, marked - marked * (Number(s.discount_pct || 0) / 100) - extra);
  const gst = exGst * Number(s.gst_rate ?? 0.15);
  const Row = ({ label, value, tid, tone = 'text-slate-600' }) => (
    <div className={`flex justify-between gap-3 text-[11px] ${tone}`}>
      <span>{label}</span><span className="font-mono-num" data-testid={tid}>{value}</span>
    </div>
  );
  return (
    <div data-testid={testid} className="bg-white border border-[#D35400]/30 rounded-md p-3 space-y-1">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#A04000]">Quote total (as on PDF)</p>
        <a href={`/appraisals/${appraisalId}?tab=Overview`} target="_blank" rel="noreferrer"
           data-testid={`${testid}-edit-link`}
           className="text-[11px] text-[#D35400] hover:underline inline-flex items-center gap-1">
          Change markup / discounts <ExternalLink size={10} />
        </a>
      </div>
      <Row label={label} value={money(lines)} tid={`${testid}-lines`} />
      {markup !== 1 && (
        <Row label={`+ Markup ${Math.round((markup - 1) * 1000) / 10}% (internal)`} value={`+${money(marked - lines)}`} tid={`${testid}-markup`} />
      )}
      {discounts.map(d => (
        <Row key={d.key} label={`− ${d.label} ${d.percent}%`} value={`−${money(marked * d.percent / 100)}`}
             tid={`${testid}-discount-${d.key}`} tone="text-emerald-700" />
      ))}
      {extra > 0 && <Row label="− Additional discount" value={`−${money(extra)}`} tid={`${testid}-extra`} tone="text-emerald-700" />}
      <div className="border-t border-slate-200 pt-1">
        <Row label="Quote total ex GST" value={money(exGst)} tid={`${testid}-ex-gst`} tone="text-slate-900 font-bold" />
        <Row label={`+ GST ${Math.round(Number(s.gst_rate ?? 0.15) * 1000) / 10}%`} value={money(gst)} tid={`${testid}-gst`} />
        <Row label="Quote total incl GST" value={money(exGst + gst)} tid={`${testid}-inc-gst`} tone="text-[#D35400] font-bold" />
      </div>
    </div>
  );
}
